import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import format from 'date-fns/format';
import React from 'react';
import { centsToDollars } from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/utils/currency';
import { StripeDisputeSelectionFragment, StripeDisputeStatus } from 'shared/types/graphql';
import { DisputeActionMenu } from '../components/DisputeActionMenu';
import { DisputePopover } from '../components/DisputePopover';
import { DATE_FORMAT } from '../constants';
import { STRIPE_DISPUTE_REASON_MAPPING } from '../records/disputes.records';
import { StatusDescription } from '../records/status.records';
import { transactionTypeMap } from '../records/transactions.records';
import { DisplayType, IColumnSelection } from '../types';
import { formatMoney } from './formatMoney';
import { dataPlaceholder, getStatusComponent } from './formatting';

interface BaseDisputesData {
  rawData: StripeDisputeSelectionFragment[];
  displayType: DisplayType;
}

interface InteractiveDisputesData extends BaseDisputesData {
  displayType: 'Interactive';
  callbacks: {
    onPetParentClick: (id: string) => void;
    onConversationClick: (channelId: string) => void;
    onDrawerOpen: (stripeId?: string | null) => void;
  };
  columns?: never;
  hasAdminRoleAtCurrentClinic: boolean;
  isDisputeDrawerLoading: boolean;
  selectedDisputeStripeId?: string;
}

interface DownloadDisputessData extends BaseDisputesData {
  displayType: 'Download';
  callbacks?: never;
  columns: IColumnSelection[];
  hasAdminRoleAtCurrentClinic?: never;
  isDisputeDrawerLoading?: never;
  selectedDisputeStripeId?: never;
}

type BuildDisputesDataInput = InteractiveDisputesData | DownloadDisputessData;

interface BaseDisputeTableRow {
  createdAt: string;
  paidAt: string;
  disputeReason: string;
  transactionType: string;
}

interface InteractiveDisputeTableRow extends BaseDisputeTableRow {
  actions: JSX.Element;
  amount: string;
  clinicPetParentName: JSX.Element | string;
  clinicPetParentNameFiltering: string;
  manage?: JSX.Element | string;
  disputeStatus: JSX.Element | string;
}

interface DownloadDisputeTableRow extends BaseDisputeTableRow {
  amount: number;
  clinicPetParentName: string;
  disputeStatus: string;
}

export type DisputeTableRow = InteractiveDisputeTableRow | DownloadDisputeTableRow;

export const buildDisputesData = ({
  rawData,
  displayType,
  columns,
  callbacks,
  hasAdminRoleAtCurrentClinic,
  isDisputeDrawerLoading,
  selectedDisputeStripeId,
}: BuildDisputesDataInput): DisputeTableRow[] => {
  const emptyPlaceholder = dataPlaceholder[displayType];
  const isDownloadData = displayType === 'Download';
  const columnsToRemove = columns?.filter((col) => col.omit).map((col) => col.selector) as (keyof DisputeTableRow)[];

  const data: DisputeTableRow[] = [];
  for (const _data of rawData || []) {
    const paidAt = _data.stripePaymentIntent?.paidAt || _data.careEnrollmentPayment?.paidAt;
    const baseData: BaseDisputeTableRow = {
      createdAt: format(new Date(_data.createdAt), DATE_FORMAT),
      paidAt: !!paidAt ? format(new Date(paidAt), DATE_FORMAT) : emptyPlaceholder,
      disputeReason: _data.stripeDisputeReason
        ? STRIPE_DISPUTE_REASON_MAPPING[_data.stripeDisputeReason]
        : emptyPlaceholder,
      transactionType: _data.stripePaymentIntent?.financialTransaction?.type
        ? transactionTypeMap[_data.stripePaymentIntent?.financialTransaction?.type]
        : 'Subscription',
    };

    const clinicPetParent =
      _data.stripePaymentIntent?.clinicPetParent ||
      _data.careEnrollmentPayment?.stripeInvoice?.stripeCustomer?.organizationPetParents[0]?.clinicPetParents[0];
    const clientName = !!clinicPetParent
      ? `${clinicPetParent?.firstName} ${clinicPetParent?.lastName}`
      : emptyPlaceholder;
    const clientId = !!clinicPetParent ? clinicPetParent?.id : emptyPlaceholder;
    const channelId = _data.financialTransaction?.channelId;
    const disputeNeedsResponse =
      _data.status === StripeDisputeStatus.NeedsResponse || _data.status === StripeDisputeStatus.WarningNeedsResponse;
    const disputeStatus = getStatusComponent({ status: _data.status });
    const stripeId = _data.associatedTransferStripePaymentId;
    const isCareDispute = !!_data.careEnrollmentPayment?.id;
    const isManageDisputeDisabled = !hasAdminRoleAtCurrentClinic || !stripeId;

    const interactiveData: InteractiveDisputeTableRow = {
      ...baseData,
      actions: (
        <DisputeActionMenu
          channelId={channelId ?? undefined}
          onConversationClick={callbacks?.onConversationClick}
          stripePaymentIntentId={_data.stripePaymentIntent?.id}
          invoiceId={_data.stripePaymentIntent?.invoices[0]?.id}
        />
      ),
      amount: formatMoney(_data.amount),
      clinicPetParentName: clinicPetParent ? (
        <Tooltip label={clientName}>
          <Link
            size="sm"
            variant="success"
            onClick={(): void => {
              if (callbacks?.onPetParentClick) {
                callbacks.onPetParentClick(clientId);
              }
            }}
          >
            {clientName}
          </Link>
        </Tooltip>
      ) : (
        emptyPlaceholder
      ),
      clinicPetParentNameFiltering: clientName,
      manage: disputeNeedsResponse ? (
        <Tooltip
          label={
            !hasAdminRoleAtCurrentClinic
              ? 'Dispute management is only available for clinic admins'
              : isCareDispute
              ? 'Care disputes are temporarily managed by providing details and evidence to kennedy@otto.vet.'
              : 'Dispute management is temporarily disabled, please contact Otto Support.'
          }
          isDisabled={!isCareDispute && !isManageDisputeDisabled}
        >
          <Box>
            <Button
              size="sm"
              variant="ghost"
              isLoading={selectedDisputeStripeId === stripeId ? isDisputeDrawerLoading : false}
              isDisabled={isCareDispute || isManageDisputeDisabled}
              onClick={(): void => {
                if (callbacks?.onDrawerOpen) {
                  callbacks?.onDrawerOpen(stripeId);
                }
              }}
            >
              Manage Dispute
            </Button>
          </Box>
        </Tooltip>
      ) : (
        emptyPlaceholder
      ),
      disputeStatus: disputeNeedsResponse ? (
        <Flex alignItems="center">
          {disputeStatus}
          <DisputePopover
            stripeId={stripeId}
            hasAdminRoleAtCurrentClinic={!!hasAdminRoleAtCurrentClinic}
            isDisputeDrawerLoading={!!isDisputeDrawerLoading}
            isCareDispute={isCareDispute}
            onSubmit={(): void => {
              if (callbacks?.onDrawerOpen) {
                callbacks?.onDrawerOpen(stripeId);
              }
            }}
          />
        </Flex>
      ) : (
        disputeStatus
      ),
    };

    const downloadData: DownloadDisputeTableRow = {
      ...baseData,
      amount: centsToDollars(_data.amount),
      clinicPetParentName: clientName,
      disputeStatus: StatusDescription[_data.status],
    };

    const finalRow: DisputeTableRow = {
      ...(isDownloadData ? downloadData : interactiveData),
    };

    if (isDownloadData && !!columnsToRemove.length) {
      columnsToRemove.forEach((column) => delete finalRow[column]);
    }

    data.push(finalRow);
  }

  return data;
};
